/**
 * Citron Luxury Product Page
 *
 * Vanilla custom elements — no dependencies, no framework.
 * Cart submission is deliberately NOT handled here: the buy buttons are wrapped
 * in Horizon's native <product-form-component>, so add-to-cart, cart drawer
 * refresh and section rendering all stay on the theme's own code path.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Safely parse a JSON <script> tag inside a host element. */
  function readJSON(host, selector) {
    var el = host.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /* ======================================================================
     citron-gallery
     Scroll-snap slider: free swipe on mobile, thumbs/arrows on desktop,
     hover zoom, and variant-driven media switching.
     ====================================================================== */

  class CitronGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ctr-track]');
      if (!this.track) return;

      this.slides = Array.from(this.querySelectorAll('[data-ctr-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-ctr-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-ctr-dot]'));
      this.prevBtn = this.querySelector('[data-ctr-prev]');
      this.nextBtn = this.querySelector('[data-ctr-next]');
      this.index = 0;

      this.thumbs.forEach(
        function (thumb, i) {
          thumb.addEventListener('click', this.select.bind(this, i, true));
        }.bind(this)
      );

      this.dots.forEach(
        function (dot, i) {
          dot.addEventListener('click', this.select.bind(this, i, true));
        }.bind(this)
      );

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.step.bind(this, -1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.step.bind(this, 1));

      // Keep the active state in sync with manual swiping.
      this.onScroll = this.debounce(this.syncFromScroll.bind(this), 90);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.hasAttribute('data-zoom')) this.initZoom();

      // Variant image switching, broadcast by <citron-variants>.
      this.onVariantMedia = this.handleVariantMedia.bind(this);
      document.addEventListener('citron:variant-media', this.onVariantMedia);

      this.updateNav();
    }

    disconnectedCallback() {
      document.removeEventListener('citron:variant-media', this.onVariantMedia);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    }

    debounce(fn, wait) {
      var t;
      return function () {
        clearTimeout(t);
        t = setTimeout(fn, wait);
      };
    }

    /** Move to a slide. `scroll` false is used when reacting to a scroll event. */
    select(i, scroll) {
      if (i < 0 || i >= this.slides.length) return;
      this.index = i;

      if (scroll !== false) {
        var slide = this.slides[i];
        this.track.scrollTo({
          left: slide.offsetLeft - this.track.offsetLeft,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      }

      this.thumbs.forEach(function (t, n) {
        t.setAttribute('aria-current', n === i ? 'true' : 'false');
      });
      this.dots.forEach(function (d, n) {
        d.setAttribute('aria-current', n === i ? 'true' : 'false');
      });

      this.pauseInactiveVideos();
      this.updateNav();
    }

    step(dir) {
      this.select(this.index + dir, true);
    }

    syncFromScroll() {
      var mid = this.track.scrollLeft + this.track.clientWidth / 2;
      var closest = 0;
      var best = Infinity;

      this.slides.forEach(
        function (slide, i) {
          var center = slide.offsetLeft - this.track.offsetLeft + slide.offsetWidth / 2;
          var dist = Math.abs(center - mid);
          if (dist < best) {
            best = dist;
            closest = i;
          }
        }.bind(this)
      );

      if (closest !== this.index) this.select(closest, false);
    }

    updateNav() {
      if (this.prevBtn) this.prevBtn.hidden = this.index <= 0;
      if (this.nextBtn) this.nextBtn.hidden = this.index >= this.slides.length - 1;
    }

    /** Stop any video that scrolled out of view. */
    pauseInactiveVideos() {
      this.slides.forEach(
        function (slide, i) {
          if (i === this.index) return;
          var vid = slide.querySelector('video');
          if (vid && !vid.paused) vid.pause();
        }.bind(this)
      );
    }

    initZoom() {
      var scale = this.getAttribute('data-zoom-scale') || '1.8';
      this.style.setProperty('--ctr-zoom-scale', scale);

      this.slides.forEach(function (slide) {
        if (!slide.classList.contains('ctr-gallery__slide--image')) return;

        slide.addEventListener('mouseenter', function () {
          slide.classList.add('is-zoomed');
        });
        slide.addEventListener('mouseleave', function () {
          slide.classList.remove('is-zoomed');
        });
        slide.addEventListener('mousemove', function (e) {
          var r = slide.getBoundingClientRect();
          var x = ((e.clientX - r.left) / r.width) * 100;
          var y = ((e.clientY - r.top) / r.height) * 100;
          slide.style.setProperty('--ctr-zoom-x', x + '%');
          slide.style.setProperty('--ctr-zoom-y', y + '%');
        });
      });
    }

    handleVariantMedia(event) {
      if (event.detail.galleryId !== this.id) return;
      var mediaId = String(event.detail.mediaId || '');
      if (!mediaId) return;

      for (var i = 0; i < this.slides.length; i++) {
        if (this.slides[i].getAttribute('data-media-id') === mediaId) {
          this.select(i, true);
          return;
        }
      }
    }
  }

  /* ======================================================================
     citron-variants
     Resolves the selected variant client-side (no section re-render) and
     updates price, availability, the form's variant id, gallery and URL.
     ====================================================================== */

  class CitronVariants extends HTMLElement {
    connectedCallback() {
      this.data = readJSON(this, '[data-ctr-variant-data]');
      if (!this.data) return;

      this.variants = this.data.variants || [];
      this.optionCount = this.data.option_count || 0;
      this.galleryId = this.getAttribute('data-gallery-id') || '';
      this.updateUrl = this.getAttribute('data-update-url') === 'true';

      this.addEventListener('change', this.onChange.bind(this));
      this.onChange();
    }

    /** Current selection, read straight off the checked inputs / selects. */
    selectedOptions() {
      var values = [];
      for (var i = 0; i < this.optionCount; i++) {
        var checked = this.querySelector('input[name="ctr-option-' + i + '"]:checked');
        if (checked) {
          values.push(checked.value);
          continue;
        }
        var select = this.querySelector('select[name="ctr-option-' + i + '"]');
        values.push(select ? select.value : null);
      }
      return values;
    }

    findVariant(selected) {
      return (
        this.variants.find(function (v) {
          return v.options.every(function (opt, i) {
            return opt === selected[i];
          });
        }) || null
      );
    }

    onChange() {
      var selected = this.selectedOptions();
      var variant = this.findVariant(selected);

      this.markUnavailable(selected);
      this.reflect(variant);

      document.dispatchEvent(
        new CustomEvent('citron:variant-change', {
          bubbles: true,
          detail: { variant: variant, sectionId: this.getAttribute('data-section-id') },
        })
      );

      if (variant && this.galleryId && variant.featured_media_id) {
        document.dispatchEvent(
          new CustomEvent('citron:variant-media', {
            detail: { galleryId: this.galleryId, mediaId: variant.featured_media_id },
          })
        );
      }

      if (variant && this.updateUrl && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    /**
     * Disable option values that cannot form an available combination with
     * everything selected before them.
     */
    markUnavailable(selected) {
      for (var i = 0; i < this.optionCount; i++) {
        var inputs = this.querySelectorAll('input[name="ctr-option-' + i + '"]');

        Array.prototype.forEach.call(
          inputs,
          function (index, input) {
            var candidate = selected.slice();
            candidate[index] = input.value;

            var reachable = this.variants.some(function (v) {
              if (!v.available) return false;
              for (var k = 0; k <= index; k++) {
                if (candidate[k] != null && v.options[k] !== candidate[k]) return false;
              }
              return true;
            });

            input.disabled = !reachable;
          }.bind(this, i)
        );
      }
    }

    /** Push the resolved variant into the DOM. */
    reflect(variant) {
      var root = document.getElementById(this.getAttribute('data-scope-id')) || document;

      // Hidden input consumed by Horizon's <product-form-component>.
      var idInput = root.querySelector('[ref="variantId"]');
      if (idInput) idInput.value = variant ? variant.id : '';

      var setHTML = function (sel, html) {
        var el = root.querySelector(sel);
        if (el) el.innerHTML = html;
      };
      var toggle = function (sel, show) {
        var el = root.querySelector(sel);
        if (el) el.hidden = !show;
      };

      if (variant) {
        setHTML('[data-ctr-price-current]', variant.price);
        setHTML('[data-ctr-price-compare]', variant.compare_at_price || '');
        toggle('[data-ctr-price-compare]', !!variant.compare_at_price);
        setHTML('[data-ctr-price-save]', variant.savings_label || '');
        toggle('[data-ctr-price-save]', !!variant.savings_label);
        setHTML('[data-ctr-sku]', variant.sku || '');
      }

      // Selected value labels next to each option name.
      var selected = this.selectedOptions();
      for (var i = 0; i < this.optionCount; i++) {
        var label = this.querySelector('[data-ctr-option-value="' + i + '"]');
        if (label) label.textContent = selected[i] || '';
      }

      // Buy buttons.
      var available = variant && variant.available;
      var btn = root.querySelector('[data-ctr-atc]');
      if (btn) {
        btn.disabled = !available;
        var text = btn.querySelector('[data-ctr-atc-text]') || btn;
        if (!variant) text.textContent = btn.getAttribute('data-text-unavailable') || 'Unavailable';
        else if (!variant.available) text.textContent = btn.getAttribute('data-text-soldout') || 'Sold out';
        else text.textContent = btn.getAttribute('data-text-default') || 'Add to cart';
      }

      var payment = root.querySelector('[data-ctr-payment-button]');
      if (payment) payment.hidden = !available;

      // Mirror price and availability into the sticky bar.
      var stickyPrice = document.querySelector('[data-ctr-sticky-price]');
      if (stickyPrice && variant) stickyPrice.innerHTML = variant.price;

      var stickyBtn = document.querySelector('[data-ctr-sticky-btn]');
      if (stickyBtn) {
        stickyBtn.disabled = !available;
        if (btn) stickyBtn.textContent = (btn.querySelector('[data-ctr-atc-text]') || btn).textContent.trim();
      }
    }
  }

  /* ======================================================================
     Add-to-cart loading state

     Horizon's <product-form-component> only toggles `disabled` while a cart
     request is in flight, so the button gives no visual feedback. We mark it
     aria-busy on submit and clear it when the theme's own cart events land.
     ====================================================================== */

  function bindLoadingState() {
    var clear = function () {
      document.querySelectorAll('[data-ctr-atc][aria-busy="true"]').forEach(function (btn) {
        btn.removeAttribute('aria-busy');
      });
    };

    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || !form.matches('[data-type="add-to-cart-form"]')) return;

        var btn = form.querySelector('[data-ctr-atc]');
        if (!btn || btn.disabled) return;

        btn.setAttribute('aria-busy', 'true');
        // Safety net: never strand the spinner if no cart event arrives.
        setTimeout(clear, 8000);
      },
      true
    );

    // 'cart:update' covers both success and add; 'cart:error' covers failure.
    document.addEventListener('cart:update', clear);
    document.addEventListener('cart:error', clear);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindLoadingState);
  } else {
    bindLoadingState();
  }

  /* ======================================================================
     citron-quantity — accessible stepper
     ====================================================================== */

  class CitronQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.minus = this.querySelector('[data-ctr-minus]');
      this.plus = this.querySelector('[data-ctr-plus]');

      if (this.minus) this.minus.addEventListener('click', this.nudge.bind(this, -1));
      if (this.plus) this.plus.addEventListener('click', this.nudge.bind(this, 1));
      this.input.addEventListener('change', this.clamp.bind(this));

      this.clamp();
    }

    nudge(dir) {
      var step = parseInt(this.input.step, 10) || 1;
      this.input.value = (parseInt(this.input.value, 10) || 1) + dir * step;
      this.clamp();
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    clamp() {
      var min = parseInt(this.input.min, 10) || 1;
      var max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      var val = parseInt(this.input.value, 10);

      if (isNaN(val)) val = min;
      val = Math.max(min, Math.min(max, val));
      this.input.value = val;

      if (this.minus) this.minus.disabled = val <= min;
      if (this.plus) this.plus.disabled = val >= max;
    }
  }

  /* ======================================================================
     citron-accordion — grid-template-rows transition, no height math
     ====================================================================== */

  class CitronAccordion extends HTMLElement {
    connectedCallback() {
      this.single = this.getAttribute('data-single') === 'true';
      this.toggles = Array.from(this.querySelectorAll('[data-ctr-acc-toggle]'));

      this.toggles.forEach(
        function (btn) {
          btn.addEventListener('click', this.onToggle.bind(this, btn));
        }.bind(this)
      );
    }

    onToggle(btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;

      var willOpen = btn.getAttribute('aria-expanded') !== 'true';

      if (this.single && willOpen) {
        this.toggles.forEach(function (other) {
          if (other === btn) return;
          other.setAttribute('aria-expanded', 'false');
          var p = document.getElementById(other.getAttribute('aria-controls'));
          if (p) p.setAttribute('data-open', 'false');
        });
      }

      btn.setAttribute('aria-expanded', String(willOpen));
      panel.setAttribute('data-open', String(willOpen));
    }
  }

  /* ======================================================================
     citron-countdown — optional auto-reset when the deadline passes
     ====================================================================== */

  class CitronCountdown extends HTMLElement {
    connectedCallback() {
      this.endAttr = this.getAttribute('data-end') || '';
      this.autoReset = this.getAttribute('data-auto-reset') === 'true';
      this.resetHours = parseFloat(this.getAttribute('data-reset-hours')) || 24;
      this.hideOnEnd = this.getAttribute('data-hide-on-end') === 'true';

      this.nums = {
        days: this.querySelector('[data-ctr-days]'),
        hours: this.querySelector('[data-ctr-hours]'),
        mins: this.querySelector('[data-ctr-mins]'),
        secs: this.querySelector('[data-ctr-secs]'),
      };

      this.endTime = this.resolveEnd();
      if (!this.endTime) {
        this.remove();
        return;
      }

      this.tick();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }

    /**
     * A fixed end date is used as-is. Auto-reset rolls the deadline forward in
     * whole windows so the timer never sits dead at zero.
     */
    resolveEnd() {
      var end = this.endAttr ? new Date(this.endAttr).getTime() : NaN;

      if (isNaN(end)) {
        if (!this.autoReset) return null;
        return Date.now() + this.resetHours * 3600000;
      }

      if (this.autoReset && end <= Date.now()) {
        var window_ms = this.resetHours * 3600000;
        var elapsed = Date.now() - end;
        var cycles = Math.floor(elapsed / window_ms) + 1;
        return end + cycles * window_ms;
      }

      return end;
    }

    tick() {
      var diff = this.endTime - Date.now();

      if (diff <= 0) {
        this.setDigits(0, 0, 0, 0);
        if (this.hideOnEnd) this.hidden = true;
        this.dispatchEvent(new CustomEvent('citron:countdown-end', { bubbles: true }));
        return;
      }

      this.setDigits(
        Math.floor(diff / 86400000),
        Math.floor((diff % 86400000) / 3600000),
        Math.floor((diff % 3600000) / 60000),
        Math.floor((diff % 60000) / 1000)
      );

      this.timer = setTimeout(this.tick.bind(this), 1000);
    }

    setDigits(d, h, m, s) {
      var pad = function (n) {
        return n < 10 ? '0' + n : String(n);
      };
      if (this.nums.days) this.nums.days.textContent = pad(d);
      if (this.nums.hours) this.nums.hours.textContent = pad(h);
      if (this.nums.mins) this.nums.mins.textContent = pad(m);
      if (this.nums.secs) this.nums.secs.textContent = pad(s);
    }
  }

  /* ======================================================================
     citron-rail — arrow controls for horizontal media rails
     ====================================================================== */

  class CitronRail extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-ctr-rail]');
      if (!this.scroller) return;

      this.prev = this.querySelector('[data-ctr-rail-prev]');
      this.next = this.querySelector('[data-ctr-rail-next]');

      if (this.prev) this.prev.addEventListener('click', this.scrollBy.bind(this, -1));
      if (this.next) this.next.addEventListener('click', this.scrollBy.bind(this, 1));

      this.scroller.addEventListener('scroll', this.update.bind(this), { passive: true });
      window.addEventListener('resize', this.update.bind(this));

      // Click-to-play posters for YouTube / Vimeo facades.
      this.querySelectorAll('[data-ctr-facade]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var frame = document.createElement('iframe');
          frame.src = btn.getAttribute('data-src');
          frame.title = btn.getAttribute('data-title') || 'Video';
          frame.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
          frame.allowFullscreen = true;
          frame.loading = 'lazy';
          btn.parentNode.replaceChild(frame, btn);
        });
      });

      this.initLazyVideo();
      this.update();
    }

    /* Videos ship with preload="none" and no autoplay attribute, so the browser
       downloads nothing during page load. Playback starts the first time the
       item is actually on screen, and pauses when it scrolls away so an
       offscreen video never burns bandwidth, CPU or battery. */
    initLazyVideo() {
      var items = this.querySelectorAll('.ctr-rail__item[data-ctr-autoplay]');
      if (!items.length) return;

      // Without IntersectionObserver, fall back to playing immediately —
      // worse for bandwidth, but never a blank frame.
      if (!('IntersectionObserver' in window)) {
        items.forEach(function (item) {
          hydrate(item.querySelector('video'));
        });
        return;
      }

      function hydrate(video) {
        if (!video) return;
        var pending = video.getAttribute('data-ctr-video-src');
        if (pending) {
          var source = document.createElement('source');
          source.src = pending;
          source.type = 'video/mp4';
          video.appendChild(source);
          video.removeAttribute('data-ctr-video-src');
          video.load();
        }
        video.muted = true; // Autoplay is only permitted while muted.
        var attempt = video.play();
        if (attempt && attempt.catch) attempt.catch(function () {});
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target.querySelector('video');
            if (!video) return;
            if (entry.isIntersecting) hydrate(video);
            else if (!video.paused) video.pause();
          });
        },
        { rootMargin: '200px 0px', threshold: 0.1 }
      );

      items.forEach(function (item) {
        observer.observe(item);
      });
    }

    scrollBy(dir) {
      var item = this.scroller.querySelector('.ctr-rail__item');
      var amount = item ? item.offsetWidth + 14 : this.scroller.clientWidth * 0.8;
      this.scroller.scrollBy({ left: dir * amount, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    update() {
      var max = this.scroller.scrollWidth - this.scroller.clientWidth - 2;
      if (this.prev) this.prev.hidden = this.scroller.scrollLeft <= 2;
      if (this.next) this.next.hidden = this.scroller.scrollLeft >= max;
    }
  }

  /* ======================================================================
     citron-sticky-atc — reveals once the real buy buttons scroll away
     ====================================================================== */

  class CitronStickyAtc extends HTMLElement {
    connectedCallback() {
      var targetId = this.getAttribute('data-watch');
      var target = targetId ? document.getElementById(targetId) : null;

      if (!target || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        function (entries) {
          this.classList.toggle('is-visible', !entries[0].isIntersecting);
        }.bind(this),
        { rootMargin: '0px 0px -40% 0px' }
      );

      this.observer.observe(target);

      // Forward the sticky button to the real form's submit button.
      var btn = this.querySelector('[data-ctr-sticky-btn]');
      if (btn) {
        btn.addEventListener('click', function () {
          var real = document.querySelector('[data-ctr-atc]');
          if (real) real.click();
        });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  /* ======================================================================
     citron-related — Shopify Product Recommendations API

     Fetches this same section back through /recommendations/products so the
     list is always derived from the product being viewed. Skeleton cards hold
     the space until the real markup lands, so nothing shifts.
     ====================================================================== */

  class CitronRelated extends HTMLElement {
    connectedCallback() {
      // The API-rendered copy arrives already populated — nothing to do.
      if (this.getAttribute('data-performed') === 'true') return;

      var url = this.getAttribute('data-url');
      if (!url) return;

      fetch(url)
        .then(function (r) {
          if (!r.ok) throw new Error('Recommendations request failed');
          return r.text();
        })
        .then(
          function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var incoming = doc.querySelector('citron-related');

            if (incoming && incoming.innerHTML.trim()) {
              this.innerHTML = incoming.innerHTML;
            } else {
              // No recommendations for this product — collapse rather than
              // leaving skeletons on screen.
              this.remove();
            }
          }.bind(this)
        )
        .catch(
          function () {
            this.remove();
          }.bind(this)
        );
    }
  }

  /* ======================================================================
     citron-rp-card — variant swatches + quick buy
     ====================================================================== */

  class CitronRpCard extends HTMLElement {
    connectedCallback() {
      this.variants = readJSON(this, '[data-ctr-rp-variants]') || [];

      this.image = this.querySelector('.ctr-rp-card__img--primary');
      this.price = this.querySelector('[data-ctr-rp-price]');
      this.compare = this.querySelector('[data-ctr-rp-compare]');
      this.badge = this.querySelector('[data-ctr-rp-badge]');
      this.buy = this.querySelector('[data-ctr-rp-buy]');
      this.swatches = Array.from(this.querySelectorAll('[data-ctr-rp-swatch]'));

      this.swatches.forEach(
        function (btn) {
          btn.addEventListener('click', this.onSwatch.bind(this, btn));
        }.bind(this)
      );

      if (this.buy) this.buy.addEventListener('click', this.onBuy.bind(this));

      this.initHoverImage();
    }

    /* The hover image ships with no src, so it costs nothing until the pointer
       actually reaches the card. Bound once and then released — the swap only
       ever needs to happen a single time per card. */
    initHoverImage() {
      var img = this.querySelector('[data-ctr-hover-src]');
      if (!img) return;

      var load = function () {
        var srcset = img.getAttribute('data-ctr-hover-srcset');
        if (srcset) img.srcset = srcset.replace(/\s+/g, ' ').trim();
        img.src = img.getAttribute('data-ctr-hover-src');
        img.removeAttribute('data-ctr-hover-src');
        img.removeAttribute('data-ctr-hover-srcset');
      };

      // pointerenter covers mouse and pen but not touch, which is the point.
      this.addEventListener('pointerenter', load, { once: true });
      this.addEventListener('focusin', load, { once: true });
    }

    findVariant(id) {
      var target = String(id);
      for (var i = 0; i < this.variants.length; i++) {
        if (String(this.variants[i].id) === target) return this.variants[i];
      }
      return null;
    }

    onSwatch(btn) {
      var variant = this.findVariant(btn.getAttribute('data-variant-id'));
      if (!variant) return;

      this.swatches.forEach(function (other) {
        other.setAttribute('aria-pressed', other === btn ? 'true' : 'false');
      });

      if (this.image && variant.image) {
        this.image.src = variant.image;
        this.image.removeAttribute('srcset');
      }

      if (this.price) this.price.innerHTML = variant.price;

      if (this.compare) {
        this.compare.innerHTML = variant.compare_at_price || '';
        this.compare.hidden = !variant.compare_at_price;
      }

      if (this.badge) {
        this.badge.innerHTML = variant.discount + '% OFF';
        this.badge.hidden = variant.discount <= 0;
      }

      if (this.buy) {
        this.buy.setAttribute('data-variant-id', variant.id);
        this.buy.disabled = !variant.available;
        var label = this.buy.querySelector('[data-ctr-rp-buy-text]');
        if (label) {
          label.textContent = variant.available
            ? this.buy.getAttribute('data-label')
            : this.buy.getAttribute('data-label-soldout');
        }
      }
    }

    onBuy() {
      var btn = this.buy;
      var id = btn.getAttribute('data-variant-id');
      if (!id || btn.getAttribute('aria-busy') === 'true') return;

      var label = btn.querySelector('[data-ctr-rp-buy-text]');
      btn.setAttribute('aria-busy', 'true');

      var addUrl =
        window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url
          ? Theme.routes.cart_add_url
          : '/cart/add.js';

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('Add to cart failed');
          return r.json();
        })
        .then(
          function () {
            return fetch('/cart.js')
              .then(function (r) {
                return r.json();
              })
              .catch(function () {
                return null;
              });
          }.bind(this)
        )
        .then(
          function (cart) {
            btn.removeAttribute('aria-busy');
            if (label) label.textContent = btn.getAttribute('data-label-added');
            setTimeout(function () {
              if (label) label.textContent = btn.getAttribute('data-label');
            }, 2000);

            // Same event Horizon's own cart icon, cart items and header listen for.
            var event = new Event('cart:update', { bubbles: true });
            event.detail = {
              resource: cart,
              sourceId: 'citron-related',
              data: { source: 'citron-related', itemCount: cart ? cart.item_count : undefined },
            };
            document.dispatchEvent(event);

            // Open the theme's drawer rather than navigating away.
            var drawer = document.querySelector('cart-drawer-component');
            if (drawer && typeof drawer.open === 'function') drawer.open();
          }.bind(this)
        )
        .catch(function () {
          btn.removeAttribute('aria-busy');
          if (label) label.textContent = btn.getAttribute('data-label');
        });
    }
  }

  /* ======================================================================
     citron-recently-viewed
     Reads Horizon's own localStorage list and renders via the Search API.
     ====================================================================== */

  class CitronRecentlyViewed extends HTMLElement {
    connectedCallback() {
      var host = this.querySelector('[data-ctr-rv-target]');
      if (!host) return;

      var ids;
      try {
        // Same key Horizon's RecentlyViewed helper writes to (assets/recently-viewed-products.js).
        ids = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
      } catch (e) {
        ids = [];
      }

      var currentId = this.getAttribute('data-current-id');
      ids = ids
        .filter(function (id) {
          return String(id) !== String(currentId);
        })
        .slice(0, parseInt(this.getAttribute('data-limit'), 10) || 4);

      if (!ids.length) {
        this.hidden = true;
        return;
      }

      var query = ids
        .map(function (id) {
          return 'id:' + id;
        })
        .join(' OR ');

      var url =
        (window.Theme && window.Theme.routes && window.Theme.routes.search_url ? Theme.routes.search_url : '/search') +
        '?type=product&options[unavailable_products]=last&q=' +
        encodeURIComponent(query) +
        '&section_id=' +
        this.getAttribute('data-section-id');

      fetch(url)
        .then(function (r) {
          if (!r.ok) throw new Error('Search failed');
          return r.text();
        })
        .then(
          function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var grid = doc.querySelector('[data-ctr-rv-source]');
            if (grid && grid.children.length) {
              host.innerHTML = grid.innerHTML;
            } else {
              this.hidden = true;
            }
          }.bind(this)
        )
        .catch(
          function () {
            this.hidden = true;
          }.bind(this)
        );
    }
  }

  /* ====================================================================== */


  /* ======================================================================
     citron-promo-bar — promotional pricing, synced to variant changes
     ====================================================================== */

/* Reads the pre-formatted money strings Liquid produced and swaps them when
     the theme announces a variant change. No fetch, no currency maths in the
     browser, no second variant system. */
  class CitronPromoBar extends HTMLElement {
    connectedCallback() {
      const raw = this.querySelector('[data-ctr-promo-data]');
      try {
        this.prices = raw ? JSON.parse(raw.textContent) : null;
      } catch (e) {
        this.prices = null;
      }

      this.nowEl = this.querySelector('[data-ctr-promo-now]');
      this.wasEl = this.querySelector('[data-ctr-promo-was]');
      this.saveEl = this.querySelector('[data-ctr-promo-save]');
      this.endedEl = this.querySelector('.ctr-promo__ended');

      this.onVariant = this.onVariant.bind(this);
      document.addEventListener('citron:variant-change', this.onVariant);

      // citron-countdown fires this once it reaches zero.
      this.addEventListener('citron:countdown-end', () => {
        this.classList.add('ctr-promo--ended');
        if (this.endedEl) this.endedEl.hidden = false;
      });
    }

    disconnectedCallback() {
      document.removeEventListener('citron:variant-change', this.onVariant);
    }

    onVariant(event) {
      if (!this.prices) return;
      const variant = event.detail && event.detail.variant;
      if (!variant) return;

      const row = this.prices[String(variant.id)];
      if (!row) return;

      if (this.nowEl) this.nowEl.textContent = row.now;
      if (this.wasEl) this.wasEl.textContent = row.was;
      if (this.saveEl) this.saveEl.textContent = row.save;
    }
  }

  var registry = {
    'citron-gallery': CitronGallery,
    'citron-variants': CitronVariants,
    'citron-quantity': CitronQuantity,
    'citron-accordion': CitronAccordion,
    'citron-countdown': CitronCountdown,
    'citron-rail': CitronRail,
    'citron-sticky-atc': CitronStickyAtc,
    'citron-recently-viewed': CitronRecentlyViewed,
    'citron-related': CitronRelated,
    'citron-rp-card': CitronRpCard,
    'citron-promo-bar': CitronPromoBar,
  };

  Object.keys(registry).forEach(function (name) {
    if (!customElements.get(name)) customElements.define(name, registry[name]);
  });
})();
